/**
 * UserTable Component
 *
 * Professional data grid for displaying tenant users with sortable columns,
 * row actions menu, and selection capabilities.
 *
 * Features:
 * - Fluent UI v9 DataGrid with sortable columns
 * - Row selection with checkboxes
 * - Action menu with Edit, Reset Password, and Delete options
 * - Empty state and loading state handling
 * - Column resizing and sizing options
 * - License friendly names display
 *
 * Following pattern from TenantList component.
 */

import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Button,
  Text,
  Spinner,
  DataGrid,
  DataGridHeader,
  DataGridRow,
  DataGridHeaderCell,
  DataGridBody,
  DataGridCell,
  TableCellLayout,
  TableColumnDefinition,
  createTableColumn,
  tokens,
} from '@fluentui/react-components';
import {
  Edit20Regular,
  Delete20Regular,
  Key20Regular,
} from '@fluentui/react-icons';
import type { TenantUser, LicenseInfo } from '../../types/user';
import type { BatchAuthMethodsResponse, AuthMethodType } from '../../types/authenticationMethods';
import { useDataGridStyles } from '../common/DataGrid/hooks/useDataGridStyles';
import { createColumnWithRowActions } from '../layouts/factories/columns';
import { UserApiService } from '../../services/userApi';
import { hasAuthenticationMethod } from '../../utils/authMethodHelpers';
import { Alert } from '../common/Alert';

/** Tenant info for organization view tenant access column */
interface OrganizationTenant {
  tenant_id: string;
  tenant_name: string;
}

interface UserTableProps {
  users: TenantUser[];
  tenantId?: string;
  loading?: boolean;
  onEdit?: (user: TenantUser) => void;
  onResetPassword?: (user: TenantUser) => void;
  onDelete?: (user: TenantUser) => void;
  selectedUsers?: Set<string>;
  onSelectAll?: (checked: boolean) => void;
  onSelectUser?: (userId: string, checked: boolean) => void;
  // Privilege management - role display only (role assignment moved to Edit User modal)
  currentUserIsSuperadmin?: boolean;
  // Column view configuration
  columnView?: 'full' | 'auth-only' | 'organization' | 'workspace';
  // Refresh trigger
  refreshTrigger?: number;
  // Passwordless status callback
  onPasswordlessCountsChange?: (readyCount: number, notReadyCount: number) => void;
  // Organization view props
  organizationTenants?: OrganizationTenant[];
  organizationId?: number;
  onSaveTenantAccess?: (organizationId: number, userId: number, tenantIds: string[]) => Promise<void>;
}

export const UserTable: React.FC<UserTableProps> = ({
  users,
  tenantId,
  loading = false,
  onEdit,
  onResetPassword,
  onDelete,
  selectedUsers: selectedUsersProp,
  onSelectAll: onSelectAllProp,
  onSelectUser: onSelectUserProp,
  currentUserIsSuperadmin = false,
  columnView = 'full',
  refreshTrigger = 0,
  onPasswordlessCountsChange,
  organizationTenants: _organizationTenants = [],
}) => {
  const styles = useDataGridStyles();
  const [localSelectedUsers, setLocalSelectedUsers] = useState<Set<string>>(new Set());
  const [availableLicenses, setAvailableLicenses] = useState<LicenseInfo[]>([]);
  const [loadingLicenses, setLoadingLicenses] = useState(false);
  const [authMethodsData, setAuthMethodsData] = useState<BatchAuthMethodsResponse | null>(null);
  const [loadingAuthMethods, setLoadingAuthMethods] = useState(false);
  const [authMethodsError, setAuthMethodsError] = useState<string | null>(null);
  const [cachedUserIds, setCachedUserIds] = useState<string>('');

  // Use prop if provided, otherwise use local state
  const selectedUsers = selectedUsersProp !== undefined ? selectedUsersProp : localSelectedUsers;
  const setSelectedUsers = selectedUsersProp !== undefined ? undefined : setLocalSelectedUsers;

  // Fetch available licenses when tenant changes
  useEffect(() => {
    const fetchLicenses = async () => {
      if (!tenantId) {
        setAvailableLicenses([]);
        setLoadingLicenses(false);
        return;
      }

      setLoadingLicenses(true);
      try {
        const licenses = await UserApiService.getAvailableLicenses(tenantId);
        setAvailableLicenses(licenses);
      } catch {
        // Silently fail - licenses will show as SKU IDs if fetch fails
        setAvailableLicenses([]);
      } finally {
        setLoadingLicenses(false);
      }
    };

    fetchLicenses();
  }, [tenantId]);

  // Clear auth methods cache when refresh button is clicked
  useEffect(() => {
    if (refreshTrigger > 0) {
      setAuthMethodsData(null);
      setCachedUserIds('');
    }
  }, [refreshTrigger]);

  // Fetch authentication methods when in auth-only view
  useEffect(() => {
    const fetchAuthMethods = async () => {
      // Skip fetch if not in auth-only view or no users
      if (columnView !== 'auth-only' || !tenantId || users.length === 0) {
        return;
      }

      // Create a stable identifier for current user list
      const userIds = users.map(user => user.id).sort();
      const currentUserIds = userIds.join(',');

      // Skip if we already have data for these exact users
      if (authMethodsData !== null && cachedUserIds === currentUserIds) {
        return;
      }

      setLoadingAuthMethods(true);
      setAuthMethodsError(null);

      try {
        const data = await UserApiService.getBatchAuthenticationMethods(tenantId, userIds);
        setAuthMethodsData(data);
        setCachedUserIds(currentUserIds);
      } catch (error) {
        setAuthMethodsError(error instanceof Error ? error.message : 'Failed to fetch authentication methods');
        setAuthMethodsData(null);
        setCachedUserIds('');
      } finally {
        setLoadingAuthMethods(false);
      }
    };

    fetchAuthMethods();
  }, [columnView, tenantId, users, authMethodsData, cachedUserIds]);

  // Calculate and notify parent of passwordless counts
  useEffect(() => {
    if (columnView !== 'auth-only' || !authMethodsData || !onPasswordlessCountsChange) {
      return;
    }

    // High-security authentication methods (green in the grid)
    const HIGH_SECURITY_METHODS: AuthMethodType[] = [
      '#microsoft.graph.fido2AuthenticationMethod',
      '#microsoft.graph.windowsHelloForBusinessAuthenticationMethod',
      '#microsoft.graph.microsoftAuthenticatorAuthenticationMethod',
      '#microsoft.graph.x509CertificateAuthenticationMethod',
    ];

    // Check if a user has at least one high-security authentication method
    const isPasswordlessReady = (userId: string): boolean => {
      return HIGH_SECURITY_METHODS.some(methodType =>
        hasAuthenticationMethod(userId, methodType, authMethodsData)
      );
    };

    const readyCount = users.filter(user => isPasswordlessReady(user.id)).length;
    const notReadyCount = users.length - readyCount;

    onPasswordlessCountsChange(readyCount, notReadyCount);
  }, [columnView, authMethodsData, users, onPasswordlessCountsChange]);

  // Helper function to get license display name by SKU ID
  const getLicenseDisplayName = (skuId: string): string => {
    const license = availableLicenses.find((lic) => lic.sku_id === skuId);
    return license?.display_name || skuId;
  };

  // Check if any users have licenses that need to be translated
  const usersHaveLicenses = users.some(
    (user) => user.assigned_licenses && user.assigned_licenses.length > 0
  );

  // Determine loading/error/empty states for rendering below headers
  const isLoadingUsers = loading || (loadingLicenses && usersHaveLicenses);
  const isLoadingAuthMethods = columnView === 'auth-only' && loadingAuthMethods;
  const hasAuthError = columnView === 'auth-only' && authMethodsError;
  const isEmpty = !isLoadingUsers && !isLoadingAuthMethods && !hasAuthError && users.length === 0;
  const showDataRows = !isLoadingUsers && !isLoadingAuthMethods && !hasAuthError && users.length > 0;

  /**
   * Column sizing options
   */
  const columnSizingOptions = {
    user: {
      minWidth: 250,
      idealWidth: 340,
      defaultWidth: 340,
    },
    user_type: {
      minWidth: 80,
      idealWidth: 120,
      defaultWidth: 120,
    },
    identities: {
      minWidth: 100,
      idealWidth: 300,
      defaultWidth: 300,
    },
    licenses: {
      minWidth: 100,
      idealWidth: 200,
      defaultWidth: 200,
    },
    app_role: {
      minWidth: 100,
      idealWidth: 140,
      defaultWidth: 140,
    },
    company_name: {
      minWidth: 100,
      idealWidth: 180,
      defaultWidth: 180,
    },
    creation_type: {
      minWidth: 100,
      idealWidth: 140,
      defaultWidth: 140,
    },
    // Authentication method columns (auth-only view)
    fido2: {
      minWidth: 120,
      idealWidth: 140,
      defaultWidth: 140,
    },
    windows_hello: {
      minWidth: 120,
      idealWidth: 140,
      defaultWidth: 140,
    },
    authenticator: {
      minWidth: 120,
      idealWidth: 140,
      defaultWidth: 140,
    },
    x509_certificate: {
      minWidth: 140,
      idealWidth: 160,
      defaultWidth: 160,
    },
    tap: {
      minWidth: 80,
      idealWidth: 100,
      defaultWidth: 100,
    },
    software_oath: {
      minWidth: 120,
      idealWidth: 140,
      defaultWidth: 140,
    },
    hardware_oath: {
      minWidth: 120,
      idealWidth: 140,
      defaultWidth: 140,
    },
    phone_sms: {
      minWidth: 100,
      idealWidth: 120,
      defaultWidth: 120,
    },
    email: {
      minWidth: 80,
      idealWidth: 100,
      defaultWidth: 100,
    },
    // Organization view columns
    tenant_access: {
      minWidth: 120,
      idealWidth: 150,
      defaultWidth: 150,
    },
    organization: {
      minWidth: 120,
      idealWidth: 160,
      defaultWidth: 160,
    },
  };

  /**
   * Define table columns
   * Column visibility depends on columnView prop
   */

  // Handle DataGrid's built-in selection change
  const handleSelectionChange = (
    _event: React.SyntheticEvent,
    data: { selectedItems: Set<string | number> }
  ) => {
    // Convert the DataGrid's selection to our string Set format
    const newSelected = new Set<string>();
    data.selectedItems.forEach((id) => {
      newSelected.add(String(id));
    });

    // Update selection through local state (uncontrolled mode)
    if (setSelectedUsers) {
      setSelectedUsers(newSelected);
      return;
    }

    // Controlled mode: notify parent via callbacks
    // Detect if this is a "select all" or "deselect all" action
    const allSelected = newSelected.size === users.length && users.length > 0;
    const noneSelected = newSelected.size === 0;
    const previousAllSelected = selectedUsers.size === users.length && users.length > 0;
    const previousNoneSelected = selectedUsers.size === 0;

    // If toggling between all/none, use onSelectAll callback
    if (onSelectAllProp && ((allSelected && !previousAllSelected) || (noneSelected && !previousNoneSelected))) {
      onSelectAllProp(allSelected);
      return;
    }

    // Otherwise, find the single user that changed and call onSelectUser
    if (onSelectUserProp) {
      for (const user of users) {
        const wasSelected = selectedUsers.has(user.id);
        const isNowSelected = newSelected.has(user.id);
        if (wasSelected !== isNowSelected) {
          onSelectUserProp(user.id, isNowSelected);
          return; // Only one user changes at a time for individual clicks
        }
      }
    }
  };

  const userColumn = createColumnWithRowActions<TenantUser>({
    columnId: 'user',
    header: 'User',
    compare: (a, b) => a.display_name.localeCompare(b.display_name),
    renderContent: (user) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <Avatar
          name={user.display_name}
          size={32}
          color="colorful"
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Text weight="semibold" style={{ color: tokens.colorBrandBackgroundSelected, lineHeight: '1.2' }}>
            {user.display_name}
          </Text>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3, lineHeight: '1.2' }}>
            {user.user_principal_name}
          </Text>
        </div>
      </div>
    ),
    getActions: (user) => [
      { label: 'Edit User', icon: <Edit20Regular />, onClick: () => onEdit?.(user) },
      { label: 'Reset Password', icon: <Key20Regular />, onClick: () => onResetPassword?.(user) },
      { label: 'Delete User', icon: <Delete20Regular />, onClick: () => onDelete?.(user) },
    ],
    getAriaLabel: (user) => `Actions for ${user.display_name}`,
  });

  // Columns only in "full" view
  // Order: Organization, Licenses, Identities, User Type, Creation Type
  const fullViewColumns: TableColumnDefinition<TenantUser>[] = [
    createTableColumn<TenantUser>({
      columnId: 'organization',
      compare: (a, b) => (a.organization_name || '-').localeCompare(b.organization_name || '-'),
      renderHeaderCell: () => 'Organization',
      renderCell: (user) => (
        <TableCellLayout truncate>
          {user.organization_name || '-'}
        </TableCellLayout>
      ),
    }),
    createTableColumn<TenantUser>({
      columnId: 'licenses',
      renderHeaderCell: () => 'Licenses',
      renderCell: (user) => (
        <TableCellLayout>
          {loadingLicenses ? (
            '...'
          ) : user.assigned_licenses && user.assigned_licenses.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {user.assigned_licenses.map((skuId, idx) => (
                <Text key={idx} size={200}>{getLicenseDisplayName(skuId)}</Text>
              ))}
            </div>
          ) : '-'}
        </TableCellLayout>
      ),
    }),
    createTableColumn<TenantUser>({
      columnId: 'identities',
      renderHeaderCell: () => 'Identities',
      renderCell: (user) => (
        <TableCellLayout>
          {user.identities && user.identities.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {user.identities
                .map((identity) => identity.issuer || 'Unknown')
                .filter((val, idx, arr) => arr.indexOf(val) === idx)
                .map((issuer, idx) => (
                  <Text key={idx} size={200}>{issuer}</Text>
                ))}
            </div>
          ) : '-'}
        </TableCellLayout>
      ),
    }),
    createTableColumn<TenantUser>({
      columnId: 'user_type',
      compare: (a, b) => (a.user_type || '-').localeCompare(b.user_type || '-'),
      renderHeaderCell: () => 'User Type',
      renderCell: (user) => (
        <TableCellLayout truncate>
          {user.user_type || '-'}
        </TableCellLayout>
      ),
    }),
  ];

  // Columns only in "auth-only" view
  // Ordered by security rating: High (Green) -> Medium (Yellow) -> Low (Red)
  const authColumns: TableColumnDefinition<TenantUser>[] = [
    // FIDO2/Passkeys (High Security)
    createTableColumn<TenantUser>({
      columnId: 'fido2',
      compare: (a, b) => {
        const aHas = hasAuthenticationMethod(a.id, '#microsoft.graph.fido2AuthenticationMethod', authMethodsData);
        const bHas = hasAuthenticationMethod(b.id, '#microsoft.graph.fido2AuthenticationMethod', authMethodsData);
        return Number(aHas) - Number(bHas);
      },
      renderHeaderCell: () => (
        <div style={{
          textAlign: 'center',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'rgba(16, 124, 16, 0.25)',
            color: '#000000',
            padding: '6px 12px',
            borderRadius: '12px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            FIDO2/Passkey
          </div>
        </div>
      ),
      renderCell: (user) => {
        const hasMethod = hasAuthenticationMethod(user.id, '#microsoft.graph.fido2AuthenticationMethod', authMethodsData);
        return (
          <div style={{
            textAlign: 'center',
            width: '100%',
            backgroundColor: hasMethod ? 'rgba(16, 124, 16, 0.25)' : 'transparent',
            padding: '8px 0',
            borderRadius: hasMethod ? '12px' : '0'
          }}>
            <Text data-testid={`fido2-status-${user.id}`}>{hasMethod ? 'Yes' : 'No'}</Text>
          </div>
        );
      },
    }),
    // Windows Hello (High Security)
    createTableColumn<TenantUser>({
      columnId: 'windows_hello',
      compare: (a, b) => {
        const aHas = hasAuthenticationMethod(a.id, '#microsoft.graph.windowsHelloForBusinessAuthenticationMethod', authMethodsData);
        const bHas = hasAuthenticationMethod(b.id, '#microsoft.graph.windowsHelloForBusinessAuthenticationMethod', authMethodsData);
        return Number(aHas) - Number(bHas);
      },
      renderHeaderCell: () => (
        <div style={{
          textAlign: 'center',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'rgba(16, 124, 16, 0.25)',
            color: '#000000',
            padding: '6px 12px',
            borderRadius: '12px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            Windows Hello
          </div>
        </div>
      ),
      renderCell: (user) => {
        const hasMethod = hasAuthenticationMethod(user.id, '#microsoft.graph.windowsHelloForBusinessAuthenticationMethod', authMethodsData);
        return (
          <div style={{
            textAlign: 'center',
            width: '100%',
            backgroundColor: hasMethod ? 'rgba(16, 124, 16, 0.25)' : 'transparent',
            padding: '8px 0',
            borderRadius: hasMethod ? '12px' : '0'
          }}>
            <Text data-testid={`windows-hello-status-${user.id}`}>{hasMethod ? 'Yes' : 'No'}</Text>
          </div>
        );
      },
    }),
    // Microsoft Authenticator (High Security)
    createTableColumn<TenantUser>({
      columnId: 'authenticator',
      compare: (a, b) => {
        const aHas = hasAuthenticationMethod(a.id, '#microsoft.graph.microsoftAuthenticatorAuthenticationMethod', authMethodsData);
        const bHas = hasAuthenticationMethod(b.id, '#microsoft.graph.microsoftAuthenticatorAuthenticationMethod', authMethodsData);
        return Number(aHas) - Number(bHas);
      },
      renderHeaderCell: () => (
        <div style={{
          textAlign: 'center',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'rgba(16, 124, 16, 0.25)',
            color: '#000000',
            padding: '6px 12px',
            borderRadius: '12px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            Authenticator
          </div>
        </div>
      ),
      renderCell: (user) => {
        const hasMethod = hasAuthenticationMethod(user.id, '#microsoft.graph.microsoftAuthenticatorAuthenticationMethod', authMethodsData);
        return (
          <div style={{
            textAlign: 'center',
            width: '100%',
            backgroundColor: hasMethod ? 'rgba(16, 124, 16, 0.25)' : 'transparent',
            padding: '8px 0',
            borderRadius: hasMethod ? '12px' : '0'
          }}>
            <Text data-testid={`authenticator-status-${user.id}`}>{hasMethod ? 'Yes' : 'No'}</Text>
          </div>
        );
      },
    }),
    // X.509 Certificate (High Security)
    createTableColumn<TenantUser>({
      columnId: 'x509_certificate',
      compare: (a, b) => {
        const aHas = hasAuthenticationMethod(a.id, '#microsoft.graph.x509CertificateAuthenticationMethod', authMethodsData);
        const bHas = hasAuthenticationMethod(b.id, '#microsoft.graph.x509CertificateAuthenticationMethod', authMethodsData);
        return Number(aHas) - Number(bHas);
      },
      renderHeaderCell: () => (
        <div style={{
          textAlign: 'center',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'rgba(16, 124, 16, 0.25)',
            color: '#000000',
            padding: '6px 12px',
            borderRadius: '12px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            X.509 Certificate
          </div>
        </div>
      ),
      renderCell: (user) => {
        const hasMethod = hasAuthenticationMethod(user.id, '#microsoft.graph.x509CertificateAuthenticationMethod', authMethodsData);
        return (
          <div style={{
            textAlign: 'center',
            width: '100%',
            backgroundColor: hasMethod ? 'rgba(16, 124, 16, 0.25)' : 'transparent',
            padding: '8px 0',
            borderRadius: hasMethod ? '12px' : '0'
          }}>
            <Text data-testid={`x509-certificate-status-${user.id}`}>{hasMethod ? 'Yes' : 'No'}</Text>
          </div>
        );
      },
    }),
    // TAP (Medium Security)
    createTableColumn<TenantUser>({
      columnId: 'tap',
      compare: (a, b) => {
        const aHas = hasAuthenticationMethod(a.id, '#microsoft.graph.temporaryAccessPassAuthenticationMethod', authMethodsData);
        const bHas = hasAuthenticationMethod(b.id, '#microsoft.graph.temporaryAccessPassAuthenticationMethod', authMethodsData);
        return Number(aHas) - Number(bHas);
      },
      renderHeaderCell: () => (
        <div style={{
          textAlign: 'center',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'rgba(255, 213, 79, 0.25)',
            color: '#000000',
            padding: '6px 12px',
            borderRadius: '12px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            TAP
          </div>
        </div>
      ),
      renderCell: (user) => {
        const hasMethod = hasAuthenticationMethod(user.id, '#microsoft.graph.temporaryAccessPassAuthenticationMethod', authMethodsData);
        return (
          <div style={{
            textAlign: 'center',
            width: '100%',
            backgroundColor: hasMethod ? 'rgba(255, 213, 79, 0.25)' : 'transparent',
            padding: '8px 0',
            borderRadius: hasMethod ? '12px' : '0'
          }}>
            <Text data-testid={`tap-status-${user.id}`}>{hasMethod ? 'Yes' : 'No'}</Text>
          </div>
        );
      },
    }),
    // Software OATH (Medium Security)
    createTableColumn<TenantUser>({
      columnId: 'software_oath',
      compare: (a, b) => {
        const aHas = hasAuthenticationMethod(a.id, '#microsoft.graph.softwareOathAuthenticationMethod', authMethodsData);
        const bHas = hasAuthenticationMethod(b.id, '#microsoft.graph.softwareOathAuthenticationMethod', authMethodsData);
        return Number(aHas) - Number(bHas);
      },
      renderHeaderCell: () => (
        <div style={{
          textAlign: 'center',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'rgba(255, 213, 79, 0.25)',
            color: '#000000',
            padding: '6px 12px',
            borderRadius: '12px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            Software OATH
          </div>
        </div>
      ),
      renderCell: (user) => {
        const hasMethod = hasAuthenticationMethod(user.id, '#microsoft.graph.softwareOathAuthenticationMethod', authMethodsData);
        return (
          <div style={{
            textAlign: 'center',
            width: '100%',
            backgroundColor: hasMethod ? 'rgba(255, 213, 79, 0.25)' : 'transparent',
            padding: '8px 0',
            borderRadius: hasMethod ? '12px' : '0'
          }}>
            <Text data-testid={`software-oath-status-${user.id}`}>{hasMethod ? 'Yes' : 'No'}</Text>
          </div>
        );
      },
    }),
    // Hardware OATH Token (Medium Security)
    createTableColumn<TenantUser>({
      columnId: 'hardware_oath',
      compare: (a, b) => {
        const aHas = hasAuthenticationMethod(a.id, '#microsoft.graph.hardwareOathAuthenticationMethod', authMethodsData);
        const bHas = hasAuthenticationMethod(b.id, '#microsoft.graph.hardwareOathAuthenticationMethod', authMethodsData);
        return Number(aHas) - Number(bHas);
      },
      renderHeaderCell: () => (
        <div style={{
          textAlign: 'center',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'rgba(255, 213, 79, 0.25)',
            color: '#000000',
            padding: '6px 12px',
            borderRadius: '12px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            Hardware OATH
          </div>
        </div>
      ),
      renderCell: (user) => {
        const hasMethod = hasAuthenticationMethod(user.id, '#microsoft.graph.hardwareOathAuthenticationMethod', authMethodsData);
        return (
          <div style={{
            textAlign: 'center',
            width: '100%',
            backgroundColor: hasMethod ? 'rgba(255, 213, 79, 0.25)' : 'transparent',
            padding: '8px 0',
            borderRadius: hasMethod ? '12px' : '0'
          }}>
            <Text data-testid={`hardware-oath-status-${user.id}`}>{hasMethod ? 'Yes' : 'No'}</Text>
          </div>
        );
      },
    }),
    // Phone/SMS (Low Security)
    createTableColumn<TenantUser>({
      columnId: 'phone_sms',
      compare: (a, b) => {
        const aHas = hasAuthenticationMethod(a.id, '#microsoft.graph.phoneAuthenticationMethod', authMethodsData);
        const bHas = hasAuthenticationMethod(b.id, '#microsoft.graph.phoneAuthenticationMethod', authMethodsData);
        return Number(aHas) - Number(bHas);
      },
      renderHeaderCell: () => (
        <div style={{
          textAlign: 'center',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'rgba(196, 49, 75, 0.25)',
            color: '#000000',
            padding: '6px 12px',
            borderRadius: '12px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            Phone/SMS
          </div>
        </div>
      ),
      renderCell: (user) => {
        const hasMethod = hasAuthenticationMethod(user.id, '#microsoft.graph.phoneAuthenticationMethod', authMethodsData);
        return (
          <div style={{
            textAlign: 'center',
            width: '100%',
            backgroundColor: hasMethod ? 'rgba(196, 49, 75, 0.25)' : 'transparent',
            padding: '8px 0',
            borderRadius: hasMethod ? '12px' : '0'
          }}>
            <Text data-testid={`phone-sms-status-${user.id}`}>{hasMethod ? 'Yes' : 'No'}</Text>
          </div>
        );
      },
    }),
    // Email (Low Security)
    createTableColumn<TenantUser>({
      columnId: 'email',
      compare: (a, b) => {
        const aHas = hasAuthenticationMethod(a.id, '#microsoft.graph.emailAuthenticationMethod', authMethodsData);
        const bHas = hasAuthenticationMethod(b.id, '#microsoft.graph.emailAuthenticationMethod', authMethodsData);
        return Number(aHas) - Number(bHas);
      },
      renderHeaderCell: () => (
        <div style={{
          textAlign: 'center',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'rgba(196, 49, 75, 0.25)',
            color: '#000000',
            padding: '6px 12px',
            borderRadius: '12px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
          }}>
            Email
          </div>
        </div>
      ),
      renderCell: (user) => {
        const hasMethod = hasAuthenticationMethod(user.id, '#microsoft.graph.emailAuthenticationMethod', authMethodsData);
        return (
          <div style={{
            textAlign: 'center',
            width: '100%',
            backgroundColor: hasMethod ? 'rgba(196, 49, 75, 0.25)' : 'transparent',
            padding: '8px 0',
            borderRadius: hasMethod ? '12px' : '0'
          }}>
            <Text data-testid={`email-status-${user.id}`}>{hasMethod ? 'Yes' : 'No'}</Text>
          </div>
        );
      },
    }),
  ];

  // Columns only in "organization" view
  // Shows: User, Role, Licenses, Tenant Access
  // Does NOT show: Organization, User Type, Identities, Company Name, Creation Type
  const organizationColumns: TableColumnDefinition<TenantUser>[] = [
    // Role column (first after User)
    createTableColumn<TenantUser>({
      columnId: 'app_role',
      renderHeaderCell: () => 'Role',
      renderCell: (user) => {
        const text = getRoleDisplayText(user.role);
        return (
          <TableCellLayout>
            <Text data-testid={`app-role-text-${user.id}`}>{text}</Text>
          </TableCellLayout>
        );
      },
    }),
    // Licenses column
    createTableColumn<TenantUser>({
      columnId: 'licenses',
      renderHeaderCell: () => 'Licenses',
      renderCell: (user) => (
        <TableCellLayout>
          {loadingLicenses ? (
            '...'
          ) : user.assigned_licenses && user.assigned_licenses.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {user.assigned_licenses.map((skuId, idx) => (
                <Text key={idx} size={200}>{getLicenseDisplayName(skuId)}</Text>
              ))}
            </div>
          ) : '-'}
        </TableCellLayout>
      ),
    }),
    // Tenant Access column - uses accessible_tenants with pre-resolved names
    createTableColumn<TenantUser>({
      columnId: 'tenant_access',
      renderHeaderCell: () => 'Tenant Access',
      renderCell: (user) => {
        const accessibleTenants = user.accessible_tenants ?? [];
        return (
          <TableCellLayout>
            {accessibleTenants.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {accessibleTenants.map(tenant => (
                  <div key={tenant.tenant_id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Avatar
                      name={tenant.tenant_name}
                      size={24}
                      color="colorful"
                    />
                    <Text>{tenant.tenant_name}</Text>
                  </div>
                ))}
              </div>
            ) : (
              <Text>-</Text>
            )}
          </TableCellLayout>
        );
      },
    }),
  ];

  // Columns only in "workspace" view
  // Shows: Name, UPN, Licenses, Role, Actions
  // Does NOT show: User Type, Identities, Company Name, Creation Type
  const workspaceColumns: TableColumnDefinition<TenantUser>[] = [
    // Licenses column
    createTableColumn<TenantUser>({
      columnId: 'licenses',
      renderHeaderCell: () => 'Licenses',
      renderCell: (user) => (
        <TableCellLayout>
          {loadingLicenses ? (
            '...'
          ) : user.assigned_licenses && user.assigned_licenses.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {user.assigned_licenses.map((skuId, idx) => (
                <Text key={idx} size={200}>{getLicenseDisplayName(skuId)}</Text>
              ))}
            </div>
          ) : '-'}
        </TableCellLayout>
      ),
    }),
    // Role column (always visible in workspace view)
    createTableColumn<TenantUser>({
      columnId: 'app_role',
      renderHeaderCell: () => 'Role',
      renderCell: (user) => {
        const text = getRoleDisplayText(user.role);
        return (
          <TableCellLayout>
            <Text data-testid={`app-role-text-${user.id}`}>{text}</Text>
          </TableCellLayout>
        );
      },
    }),
  ];

  // Build columns based on view (selection handled by DataGrid's built-in selectionMode)
  const baseColumns: TableColumnDefinition<TenantUser>[] = [
    userColumn,
    ...(columnView === 'full' ? fullViewColumns : []),
    ...(columnView === 'auth-only' ? authColumns : []),
    ...(columnView === 'organization' ? organizationColumns : []),
    ...(columnView === 'workspace' ? workspaceColumns : []),
  ];

  /**
   * Get display text for a role value.
   */
  const getRoleDisplayText = (role: string | null): string => {
    switch (role) {
      case 'global_admin':
        return 'Global Admin';
      case 'organization_owner':
        return 'Org Owner';
      case 'organization_admin':
        return 'Org Admin';
      case 'organization_user':
        return 'Org User';
      case 'managed_tenant_user':
        return 'Tenant User';
      default:
        return 'User';
    }
  };

  /**
   * Role column - only visible to superadmins
   */
  const roleColumn = createTableColumn<TenantUser>({
    columnId: 'app_role',
    renderHeaderCell: () => 'Role',
    renderCell: (user) => {
      const text = getRoleDisplayText(user.role);
      return (
        <TableCellLayout>
          <Text data-testid={`app-role-text-${user.id}`}>{text}</Text>
        </TableCellLayout>
      );
    },
  });

  /**
   * Final columns array - conditionally includes Role column for superadmins
   * Note: Organization and Workspace views already include Role column in their columns
   * Actions are now included in the User column via hover-triggered menu
   */
  const columns = (() => {
    // Organization and Workspace views already have Role column built-in
    if (columnView === 'organization' || columnView === 'workspace') {
      return baseColumns;
    }
    // Full view: Role column after User for superadmins
    if (columnView === 'full' && currentUserIsSuperadmin) {
      return [userColumn, roleColumn, ...fullViewColumns];
    }
    // Other views: add Role column at end only for superadmins
    return currentUserIsSuperadmin
      ? [...baseColumns, roleColumn]
      : baseColumns;
  })();

  return (
    <div className={styles.tableContainer}>
      <DataGrid
        items={showDataRows ? users : []}
        columns={columns}
        sortable
        resizableColumns
        columnSizingOptions={columnSizingOptions}
        selectionMode="multiselect"
        selectedItems={selectedUsers}
        onSelectionChange={handleSelectionChange}
        getRowId={(item) => item.id}
        className={styles.table}
        data-testid="user-table"
      >
        <DataGridHeader>
          <DataGridRow>
            {({ renderHeaderCell }) => (
              <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
            )}
          </DataGridRow>
        </DataGridHeader>
        <DataGridBody<TenantUser>>
          {({ item, rowId }) => (
            <DataGridRow<TenantUser> key={rowId} data-testid={`user-row-${item.id}`}>
              {({ renderCell }) => (
                <DataGridCell>{renderCell(item)}</DataGridCell>
              )}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
      {isLoadingUsers && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner label={loading ? "Loading users..." : "Loading licenses..."} />
        </div>
      )}
      {isLoadingAuthMethods && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Spinner label="Loading authentication methods..." />
        </div>
      )}
      {hasAuthError && (
        <div style={{ padding: '20px' }}>
          <Alert intent="error">
            <Text weight="semibold">Failed to load authentication methods</Text>
            <Text block>{authMethodsError}</Text>
            <Button
              appearance="primary"
              onClick={() => {
                setAuthMethodsError(null);
                setAuthMethodsData(null);
              }}
              style={{ marginTop: '12px' }}
            >
              Try Again
            </Button>
          </Alert>
        </div>
      )}
      {isEmpty && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px', color: tokens.colorNeutralForeground3 }}>
          <Text>No users to display</Text>
        </div>
      )}
    </div>
  );
};
